import { useState, useEffect } from "react";
import { Patient } from "@/components/PatientCard";
import { TableCell, TableRow } from "@/components/ui/table";
import { Bed } from "lucide-react";
import { usePatientTATTimer } from "@/hooks/usePatientTATTimer";
import { usePatientActivities, PatientActivity } from "@/hooks/usePatientActivities";
import { useTotalElapsedTime } from "@/hooks/useTotalElapsedTime";
import { useMDCompletionTime } from "@/hooks/useMDCompletionTime";
import { useTATConfigurations } from "@/hooks/useTATConfigurations";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

const STEP_NAMES: Record<string, string> = {
  MD: "Marked for Discharge",
  DSS: "Discharge Summary Saved",
  DSF: "Discharge Summary Finalized",
  PC_PHARMACY: "Pharmacy Clearance",
  CS: "Charge Sheet",
  BS: "Final Bill Saved",
  BA: "Bill Approved",
  PC: "Payment Completed",
  PD: "Physical Discharge",
};

interface PatientTableRowProps {
  patient: Patient;
  onClick: () => void;
}

export const PatientTableRow = ({ patient, onClick }: PatientTableRowProps) => {
  const tatTimer = usePatientTATTimer(patient.uuid || patient.id);
  const { data: activities = [] } = usePatientActivities(patient.uuid || patient.id);
  const { data: mdCompletionTime } = useMDCompletionTime(patient.uuid || patient.id);
  const totalElapsedTime = useTotalElapsedTime(mdCompletionTime, {
    dischargeStatus: patient.dischargeStatus,
    dischargeCompletionTime: (patient as any).physicalDischargeCompletionTime,
  });
  const { data: tatConfigs } = useTATConfigurations();
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      if (activities.some(a => a.status === 'in-progress')) {
        setTick(t => t + 1);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [activities]);

  const getTATColor = (status?: string) => {
    switch (status) {
      case 'green': return 'text-success';
      case 'yellow': return 'text-warning';
      case 'red': return 'text-destructive';
      case 'exceeded': return 'text-destructive font-bold';
      default: return 'text-muted-foreground';
    }
  };

  const getTATBorderClass = (status?: string) => {
    switch (status) {
      case 'green': return 'border-l-success';
      case 'yellow': return 'border-l-warning';
      case 'red': return 'border-l-destructive';
      case 'exceeded': return 'border-l-destructive';
      default: return 'border-l-border';
    }
  };

  const getTATBackgroundClass = (status?: string) => {
    switch (status) {
      case 'green': return 'bg-success/5';
      case 'yellow': return 'bg-warning/5';
      case 'red': return 'bg-destructive/5';
      case 'exceeded': return 'bg-destructive/10';
      default: return 'bg-background';
    }
  };

  const calculateActivityTATStatus = (
    activity: PatientActivity
  ): { time: string; colorClass: string } => {
    if (activity.status === 'pending') {
      return { time: '-', colorClass: 'text-muted-foreground' };
    }

    if (activity.status === 'completed' || activity.status === 'skipped') {
      if (activity.timeTakenMinutes !== null && activity.timeTakenMinutes !== undefined) {
        const hours = Math.floor(activity.timeTakenMinutes / 60);
        const minutes = Math.floor(activity.timeTakenMinutes % 60);
        return {
          time: hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`,
          colorClass: activity.status === 'completed' ? 'text-success' : 'text-muted-foreground',
        };
      }
      return { time: 'N/A', colorClass: 'text-muted-foreground' };
    }

    if (activity.status === 'in-progress') {
      const startTime = activity.assignedAt || activity.startedAt;
      if (!startTime) return { time: '-', colorClass: 'text-muted-foreground' };

      const start = new Date(startTime);
      const now = new Date();
      const diffMs = now.getTime() - start.getTime();
      const elapsedMinutes = Math.floor(diffMs / 60000);

      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
      const timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

      const tatConfig = tatConfigs?.find(cfg => cfg.workflow_step === activity.workflowStep);

      if (!tatConfig) {
        let colorClass = 'text-success';
        if (elapsedMinutes > 120) colorClass = 'text-destructive font-bold';
        else if (elapsedMinutes > 60) colorClass = 'text-destructive';
        else if (elapsedMinutes > 30) colorClass = 'text-warning';
        return { time: timeString, colorClass };
      }

      const allowedMinutes = tatConfig.allowed_tat_minutes;
      const level1Threshold = Math.floor(allowedMinutes * (tatConfig.level_1_threshold_percentage / 100));
      const level2Threshold = Math.floor(allowedMinutes * (tatConfig.level_2_threshold_percentage / 100));
      const gracePeriod = tatConfig.grace_period_minutes;

      let colorClass = 'text-success';
      if (elapsedMinutes > level2Threshold + gracePeriod) colorClass = 'text-destructive font-bold';
      else if (elapsedMinutes > level2Threshold) colorClass = 'text-destructive';
      else if (elapsedMinutes > level1Threshold) colorClass = 'text-warning';

      return { time: timeString, colorClass };
    }

    return { time: '-', colorClass: 'text-muted-foreground' };
  };

  const getStatusIndicator = (status: string) => {
    switch (status) {
      case 'completed': return { label: '✓ Done', color: 'text-success' };
      case 'in-progress': return { label: '⟳ In Progress', color: 'text-warning' };
      case 'skipped': return { label: '⨯ Skipped', color: 'text-muted-foreground' };
      default: return { label: '○ Pending', color: 'text-muted-foreground' };
    }
  };

  // Sort activities by displayOrder
  const sorted = [...activities].sort((a, b) => a.displayOrder - b.displayOrder);

  // MD activity
  const mdActivity = sorted.find(a => a.workflowStep === 'MD');

  // Current tasks: all in-progress activities
  const currentTasks = sorted.filter(a => a.workflowStep !== 'MD' && a.status === 'in-progress');

  // If none in-progress, show last completed as context
  const lastCompleted = currentTasks.length === 0
    ? [...sorted].filter(a => a.workflowStep !== 'MD' && a.status === 'completed').pop()
    : null;

  const displayCurrentTasks = currentTasks.length > 0 ? currentTasks : lastCompleted ? [lastCompleted] : [];

  // Next task: first pending after current tasks
  const nextTask = sorted.find(a => a.workflowStep !== 'MD' && a.status === 'pending');

  // Discharge status
  const getDischargeStatus = () => {
    if (patient.dischargeStatus === 'discharged') return { label: 'Discharged', variant: 'default' as const, className: 'bg-primary/10 text-primary border-primary/20' };
    if (mdActivity?.status === 'completed') return { label: 'Initiated', variant: 'default' as const, className: 'bg-success/10 text-success border-success/20' };
    return { label: 'Awaiting', variant: 'secondary' as const, className: 'bg-muted text-muted-foreground' };
  };

  const dischargeStatus = getDischargeStatus();

  const renderTaskEntry = (activity: PatientActivity) => {
    const indicator = getStatusIndicator(activity.status);
    const { time, colorClass } = calculateActivityTATStatus(activity);
    const person = activity.status === 'completed' || activity.status === 'skipped'
      ? activity.completedBy
      : activity.assignedTo;

    return (
      <div key={activity.id} className="space-y-0.5">
        <p className="text-xs font-semibold text-foreground leading-tight">
          {STEP_NAMES[activity.workflowStep] || activity.name}
        </p>
        <p className={cn("text-xs leading-tight", indicator.color)}>
          {indicator.label}
        </p>
        <p className="text-xs text-muted-foreground leading-tight truncate">
          {person || '-'}
        </p>
        <p className={cn("text-xs leading-tight font-mono", colorClass)}>
          {time}
        </p>
      </div>
    );
  };

  const renderMDEntry = (activity: PatientActivity) => {
    const indicator = getStatusIndicator(activity.status);
    const { time, colorClass } = calculateActivityTATStatus(activity);
    const person = activity.status === 'completed' || activity.status === 'skipped'
      ? activity.completedBy
      : activity.assignedTo;
    const completedAt = activity.completionTime
      ? new Date(activity.completionTime).toLocaleString('en-IN', {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: true,
        })
      : null;

    return (
      <div className="space-y-0.5">
        <p className="text-xs font-semibold text-foreground leading-tight">
          Marked for Discharge
        </p>
        <p className={cn("text-xs leading-tight", indicator.color)}>
          {indicator.label}
        </p>
        <p className="text-xs text-muted-foreground leading-tight truncate">
          {person || '-'}
        </p>
        {completedAt ? (
          <p className="text-xs text-muted-foreground leading-tight">
            {completedAt}
          </p>
        ) : (
          <p className={cn("text-xs leading-tight font-mono", colorClass)}>
            {time}
          </p>
        )}
      </div>
    );
  };

  return (
    <TableRow
      className={cn(
        "cursor-pointer hover:bg-muted/50 transition-colors border-l-4",
        getTATBorderClass(tatTimer?.status),
        getTATBackgroundClass(tatTimer?.status)
      )}
      onClick={onClick}
    >
      {/* Patient Info */}
      <TableCell className="p-2 align-middle">
        <div className="bg-card rounded-lg shadow-sm border-l-4 border-l-primary p-3 space-y-0.5">
          <p className="text-sm font-semibold text-foreground leading-tight">{patient.name}</p>
          <p className="text-xs text-muted-foreground leading-tight">
            {patient.id} {patient.age} YRS • {patient.gender}
          </p>
        </div>
      </TableCell>

      {/* Admission */}
      <TableCell className="px-4 align-middle">
        <div className="flex flex-col justify-center gap-1">
          <p className="text-sm text-foreground font-medium leading-tight">
            {(() => {
              const d = patient.admissionDateTime;
              if (!d) return patient.admissionDate;
              const day = String(d.getDate()).padStart(2, '0');
              const month = d.toLocaleString('en-US', { month: 'long' });
              const year = d.getFullYear();
              const hours = d.getHours();
              const minutes = String(d.getMinutes()).padStart(2, '0');
              const period = hours >= 12 ? 'PM' : 'AM';
              const h12 = hours % 12 || 12;
              return `${day} ${month} ${year} - ${String(h12).padStart(2, '0')}:${minutes} ${period}`;
            })()}
          </p>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground leading-tight">
            <Bed className="w-3.5 h-3.5 flex-shrink-0" />
            <span>
              Ward {patient.room?.split("")[0] || "3"}B • Room {patient.room}, Bed {patient.bed}
            </span>
          </div>
        </div>
      </TableCell>

      {/* Discharge Initiated */}
      <TableCell className="px-4 align-middle">
        {mdActivity?.completionTime ? (
          <div className="flex flex-col justify-center gap-1">
            <p className="text-sm text-foreground font-medium leading-tight">
              {(() => {
                const d = new Date(mdActivity.completionTime);
                const day = String(d.getDate()).padStart(2, '0');
                const month = d.toLocaleString('en-US', { month: 'long' });
                const year = d.getFullYear();
                const hours = d.getHours();
                const minutes = String(d.getMinutes()).padStart(2, '0');
                const period = hours >= 12 ? 'PM' : 'AM';
                const h12 = hours % 12 || 12;
                return `${day} ${month} ${year} - ${String(h12).padStart(2, '0')}:${minutes} ${period}`;
              })()}
            </p>
            <div className="text-xs text-muted-foreground leading-tight">
              <span>LOS: {patient.los || '-'}</span>
            </div>
            {(() => {
              if (!patient.expectedDischarge) return null;
              const expected = new Date(patient.expectedDischarge);
              const compareDate = patient.dischargeStatus === 'discharged' && (patient as any).physicalDischargeCompletionTime
                ? new Date((patient as any).physicalDischargeCompletionTime)
                : new Date();
              const diffDays = Math.floor((compareDate.getTime() - expected.getTime()) / (1000 * 60 * 60 * 24));
              if (diffDays <= 0) return null;
              return (
                <p className="text-xs text-destructive font-medium leading-tight">
                  Exceeded: {diffDays} {diffDays === 1 ? 'day' : 'days'}
                </p>
              );
            })()}
          </div>
        ) : (
          <div className="flex flex-col justify-center gap-1">
            <p className="text-sm text-muted-foreground font-medium leading-tight">Not Initiated</p>
            <div className="text-xs text-muted-foreground leading-tight">
              <span>LOS: {patient.los || '-'}</span>
            </div>
            {(() => {
              if (!patient.expectedDischarge) return null;
              const expected = new Date(patient.expectedDischarge);
              const now = new Date();
              const diffDays = Math.floor((now.getTime() - expected.getTime()) / (1000 * 60 * 60 * 24));
              if (diffDays <= 0) return null;
              return (
                <p className="text-xs text-destructive font-medium leading-tight">
                  Exceeded: {diffDays} {diffDays === 1 ? 'day' : 'days'}
                </p>
              );
            })()}
          </div>
        )}
      </TableCell>

      {/* Current Task */}
      <TableCell className="px-3 py-3 min-w-[200px] align-middle text-center">
        {displayCurrentTasks.length > 0 ? (
          <div className="space-y-2">
            {displayCurrentTasks.map((task, i) => (
              <div key={task.id}>
                {i > 0 && <div className="border-t border-border my-1.5" />}
                {renderTaskEntry(task)}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">-</p>
        )}
      </TableCell>


      {/* Discharge Status */}
      <TableCell className="px-3 py-3 align-middle text-center">
        <div className="space-y-1.5">
          <Badge variant={dischargeStatus.variant} className={cn("text-xs", dischargeStatus.className)}>
            {dischargeStatus.label}
          </Badge>
          <p className="text-xs text-destructive/70 leading-tight">Total Elapsed Time</p>
          <p className={cn(
            "text-xs font-mono leading-tight",
            "text-muted-foreground"
          )}>
            {totalElapsedTime || '--:--:--'}
          </p>
        </div>
      </TableCell>
    </TableRow>
  );
};
